import { ActivatedRouteSnapshot, Router } from '@angular/router';

/**
 * Persona flag a route can carry on its `data`:
 * - `'manager'`   — route is manager-only chrome; entering it forces manager persona.
 * - `'customer'`  — route forces customer persona (rarely set; that's the default).
 * - `'preserve'`  — route is bimodal; leave the current persona alone.
 *
 * Undefined = no flag = customer default (matches historic behaviour so a missing
 * tag never silently strands the user in manager-view chrome on a customer page).
 */
type RouteViewMode = 'manager' | 'customer' | 'preserve';

/**
 * Walk the currently activated route tree from root to leaf and resolve the
 * nearest `data.viewMode`. Angular's default paramsInheritanceStrategy is
 * `emptyOnly`, so a parent's `data.viewMode` does NOT merge into a non-empty
 * child's `route.data` — we have to traverse and pick up the flag wherever it
 * sits on the ancestry chain. Children override by declaring their own.
 *
 * Returns `undefined` when no route in the tree declares a `viewMode`.
 */
export function resolveActiveRouteViewMode(router: Router): RouteViewMode | undefined {
  let route: ActivatedRouteSnapshot | null = router.routerState.snapshot.root;
  let mode: RouteViewMode | undefined;
  while (route) {
    const flag = route.data['viewMode'];
    if (flag === 'manager' || flag === 'customer' || flag === 'preserve') {
      mode = flag;
    }
    route = route.firstChild;
  }
  return mode;
}
